import { useEffect, useMemo, useRef, useState } from 'react'
import contractService from '@/services/contractService'
import { Garantia } from '@/types'
import { useAuth } from '@/contexts/AuthContext'
import Button from '@/components/common/Button'

type GarantiaSelectorProps = {
  value?: number | null
  onChange: (garantia: Garantia | null) => void
  label?: string
  helperText?: string
  error?: string
  disabled?: boolean
}

const emptyCreateForm = { nombre: '', descripcion: '' }

export default function GarantiaSelector({
  value,
  onChange,
  label = 'Garantía',
  helperText,
  error,
  disabled = false,
}: GarantiaSelectorProps) {
  const { user } = useAuth()
  const canCreate = user?.role === 'admin'
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [items, setItems] = useState<Garantia[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [createMode, setCreateMode] = useState(false)
  const [createForm, setCreateForm] = useState(emptyCreateForm)
  const [createLoading, setCreateLoading] = useState(false)
  const [createError, setCreateError] = useState('')

  useEffect(() => {
    let mounted = true
    const load = async () => {
      setLoading(true)
      try {
        const res: any = await contractService.getGarantias()
        const data = res.data?.items ?? res.data ?? []
        if (mounted) {
          setItems(Array.isArray(data) ? data : [])
        }
      } catch (e) {
        if (mounted) {
          setItems([])
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false)
        setCreateMode(false)
        setCreateError('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  const selected = useMemo(() => items.find((item) => Number(item.id) === Number(value)) || null, [items, value])

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return items
    return items.filter((item) => {
      return [item.nombre, item.descripcion || '']
        .join(' ')
        .toLowerCase()
        .includes(term)
    })
  }, [items, search])

  const handleSelect = (item: Garantia | null) => {
    onChange(item)
    setOpen(false)
    setSearch('')
    setCreateMode(false)
    setCreateError('')
  }

  const handleCreate = async () => {
    setCreateError('')

    const nombre = createForm.nombre.trim()
    if (!nombre) {
      setCreateError('El nombre es requerido')
      return
    }

    setCreateLoading(true)
    try {
      const res: any = await contractService.createGarantia({
        nombre,
        descripcion: createForm.descripcion.trim() || null,
      })
      const created: Garantia = res.data?.data || res.data || res
      const normalized = created && created.id ? created : null

      if (normalized) {
        setItems((prev) => [...prev, normalized].sort((a, b) => {
          if (a.es_predefinida !== b.es_predefinida) {
            return a.es_predefinida ? -1 : 1
          }
          return a.nombre.localeCompare(b.nombre)
        }))
        handleSelect(normalized)
      }
      setCreateForm(emptyCreateForm)
    } catch (e: any) {
      setCreateError(e?.response?.data?.message || 'No se pudo crear la garantia')
    } finally {
      setCreateLoading(false)
    }
  }

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <div ref={containerRef} className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((prev) => !prev)}
          className={`w-full min-h-11 border rounded-lg px-3 py-2 text-left bg-white ${
            error ? 'border-red-500' : 'border-gray-300'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'}`}
        >
          {selected ? (
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-medium text-gray-900">{selected.nombre}</div>
                {selected.descripcion && <div className="text-xs text-gray-500">{selected.descripcion}</div>}
              </div>
              {Boolean(selected.es_predefinida) && (
                <span className="text-[11px] px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                  Predefinida
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-500">Seleccionar garantia</span>
          )}
        </button>

        {open && !disabled && (
          <div className="relative z-20 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
            <div className="p-3 border-b border-gray-200">
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar garantia"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="max-h-64 overflow-y-auto">
              {loading ? (
                <div className="px-3 py-4 text-sm text-gray-500">Cargando garantias...</div>
              ) : filteredItems.length === 0 ? (
                <div className="px-3 py-4 text-sm text-gray-500">No hay resultados</div>
              ) : (
                filteredItems.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => handleSelect(item)}
                    className={`w-full text-left px-3 py-3 border-b border-gray-100 hover:bg-gray-50 ${
                      Number(value) === Number(item.id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-gray-900">{item.nombre}</div>
                        {item.descripcion && <div className="text-xs text-gray-500">{item.descripcion}</div>}
                      </div>
                      <div className="flex items-center gap-2">
                        {Boolean(item.es_predefinida) && (
                          <span className="text-[11px] px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                            Predefinida
                          </span>
                        )}
                        {!!item.contratos_count && (
                          <span className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                            {item.contratos_count} uso(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="flex items-center justify-between gap-2 p-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Limpiar
              </button>
              {canCreate && (
                <button
                  type="button"
                  onClick={() => setCreateMode((prev) => !prev)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Agregar nueva
                </button>
              )}
            </div>

            {createMode && canCreate && (
              <div className="border-t border-gray-200 p-3 space-y-3">
                <div>
                  <input
                    value={createForm.nombre}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, nombre: e.target.value }))}
                    placeholder="Nombre"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <textarea
                    value={createForm.descripcion}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, descripcion: e.target.value }))}
                    placeholder="Descripcion"
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {createError && <p className="text-sm text-red-600">{createError}</p>}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCreateMode(false)}>
                    Cancelar
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleCreate} disabled={createLoading}>
                    {createLoading ? 'Guardando...' : 'Crear'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {helperText && !error && <p className="text-xs text-gray-500">{helperText}</p>}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  )
}
